import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { MsalInterceptor, MsalGuard, MSAL_INSTANCE, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG, 
  MsalInterceptorConfiguration, ProtectedResourceScopes, 
  MsalModule,
  MsalGuardConfiguration} from '@azure/msal-angular';
import { PublicClientApplication, InteractionType, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { enableProdMode, importProvidersFrom } from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app/app-routing.module';
import { environment } from '@environments/environment';
import { RoleGuard } from '@app/core/secure/role.guard';
import { IdentityService } from '@app/core/auth/identity.service';
import { getProtectedResources } from './app/core/auth/protected-resources.token';
import { provideEnvironmentNgxMask } from 'ngx-mask';


const isIE = window.navigator.userAgent.indexOf("MSIE ") > -1 || window.navigator.userAgent.indexOf("Trident/") > -1;

if (environment.production) {
  enableProdMode();
}

// 🔧 Dynamic MSAL instance
export function MSALInstanceFactory(): PublicClientApplication {
  return new PublicClientApplication({
  auth: {
        clientId: environment.azureId.clientId,
        authority: environment.azureId.authority,
        redirectUri: environment.azureId.redirectUri, // Points to window.location.origin by default. You must register this URI on Azure portal/App Registration.
        postLogoutRedirectUri: '/', // Points to window.location.origin by default.
        navigateToLoginRequestUrl: false,
        clientCapabilities: ['CP1'] // This lets the resource server know that this client can handle claim challenges.
    },
    cache: {
      //cacheLocation: BrowserCacheLocation.LocalStorage,
        //cacheLocation: BrowserCacheLocation.SessionStorage, // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        cacheLocation: BrowserCacheLocation.LocalStorage, // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: isIE, // Set this to "true" if you are having issues on IE11 or Edge. Remove this line to use Angular Universal
    },
    system: {
        /**
         * Below you can configure MSAL.js logs. For more information, visit:
         * https://docs.microsoft.com/azure/active-directory/develop/msal-logging-js
         */
        loggerOptions: {
            loggerCallback(level: LogLevel, message: string) {
              if (level === LogLevel.Error) {
                console.error(message);
              }
            },
            logLevel: LogLevel.Error,
            piiLoggingEnabled: false
        },
        asyncPopups: false
    }
  });
}

// 🔐 Guard config
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  const loginRequest = {
    scopes: ["api://" + environment.azureId.apiClientId + "/.default"]
  };
  return {
    interactionType: InteractionType.Redirect,
    authRequest: loginRequest
  };
}

// 🌐 Interceptor config
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, ProtectedResourceScopes[]>();

  const resources = getProtectedResources();

  protectedResourceMap.set(resources.apiWater.endpoint, [
    {
      httpMethod: 'GET',
      scopes: [...resources.apiWater.scopes.read]
    },
    {
      httpMethod: 'POST',
      scopes: [...resources.apiWater.scopes.write]
    },
    {
      httpMethod: 'PATCH',
      scopes: [...resources.apiWater.scopes.write]
    },
    {
      httpMethod: 'PUT',
      scopes: [...resources.apiWater.scopes.write]
    },
    {
      httpMethod: 'DELETE',
      scopes: [...resources.apiWater.scopes.write]
    }
  ]);

  return {
    interactionType: InteractionType.Redirect, protectedResourceMap
  };
}

(async () => {
  const identityService = new IdentityService(
    new (await import('@angular/common/http')).HttpClient(
      new (await import('@angular/common/http')).HttpXhrBackend({ build: () => new XMLHttpRequest() })
    )
  );

  try {
    await identityService.initializeAuthConfig();
    await bootstrapApplication(AppComponent, {
      providers: [
        provideHttpClient(withInterceptorsFromDi()),
        provideRouter(routes),
        provideEnvironmentNgxMask(),
        RoleGuard,
        MsalGuard,
        { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
        { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
        { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
        { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: MSALInterceptorConfigFactory },
  
        importProvidersFrom(
          MsalModule.forRoot(
            MSALInstanceFactory(),
            MSALGuardConfigFactory(),
            MSALInterceptorConfigFactory()
          )
        )
      ]
    });
    console.log('✅ MSAL config initialized and app bootstrapped');
  } catch (error) {
    console.error('❌ Failed to initialize MSAL config:', error);
    }
})();

